import type { RafflePlace, RevealAnim } from "../types";
import { binaryPick, EmptyOptionsError, randomInt, shuffle } from "./random";

export interface ToolOutcome {
  result: string;
  detail?: string;
  anim: RevealAnim;
  animValue?: any;
  reel?: string[];
  podium?: RafflePlace[];
}

export const ordinal = (n: number) => `${n}${["st", "nd", "rd"][n - 1] || "th"}`;

export function raffleDraw(tickets: number, prizes: string[]): ToolOutcome {
  const picked = new Set<number>();
  while (picked.size < prizes.length) picked.add(randomInt(1, tickets));
  const podium = [...picked].map((ticket, i) => ({ place: i + 1, ticket, prize: prizes[i] }));
  return {
    result: podium.map((p) => `${ordinal(p.place)} #${p.ticket}`).join(" · "),
    detail: `${prizes.length} prize${prizes.length > 1 ? "s" : ""} from ${tickets} tickets`,
    anim: "slot", reel: numberReel(1, tickets).map((n) => `#${n}`), podium,
  };
}

const SUITS = [
  { s: "♥", red: true },
  { s: "♦", red: true },
  { s: "♠", red: false },
  { s: "♣", red: false },
];
const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

const numberReel = (min: number, max: number) => Array.from({ length: 12 }, () => String(randomInt(min, max)));

export function flipCoin(): ToolOutcome {
  const r = binaryPick("HEADS", "TAILS");
  return { result: r, anim: "coin", animValue: r };
}

export function rollDice(count: number): ToolOutcome {
  const n = Math.min(3, Math.max(1, count));
  const values = Array.from({ length: n }, () => randomInt(1, 6));
  const total = values.reduce((a, b) => a + b, 0);
  return { result: String(total), detail: n > 1 ? `${values.join(" + ")} = ${total}` : undefined, anim: "dice", animValue: values };
}

export function redOrBlack(): ToolOutcome {
  const suit = SUITS[randomInt(0, 3)];
  const rank = RANKS[randomInt(0, 12)];
  return { result: suit.red ? "RED" : "BLACK", detail: `DITHER drew the ${rank}${suit.s}`, anim: "card", animValue: { rank, suit: suit.s, red: suit.red } };
}

export function oddOrEven(): ToolOutcome {
  const n = randomInt(1, 100);
  return { result: n % 2 ? "ODD" : "EVEN", detail: `DITHER rolled ${n}`, anim: "slot", reel: numberReel(1, 100) };
}

export function thisOrThat(a: string, b: string): ToolOutcome {
  const x = a.trim();
  const y = b.trim();
  if (!x || !y) throw new EmptyOptionsError("Fill in both options first.");
  if (x.toLowerCase() === y.toLowerCase()) throw new EmptyOptionsError("Those are the same thing, silly. Pick two different options.");
  const result = binaryPick(x, y);
  return { result, detail: `over ${result === x ? y : x}`, anim: "slot", reel: shuffle([x, y, x, y, x, y]) };
}

export function validateRange(minRaw: string, maxRaw: string): { min: number; max: number } {
  const min = Number(minRaw);
  const max = Number(maxRaw);
  if (!Number.isInteger(min) || !Number.isInteger(max)) throw new EmptyOptionsError("Use whole numbers only.");
  if (Math.abs(min) > 1_000_000 || Math.abs(max) > 1_000_000) throw new EmptyOptionsError("Keep numbers within ±1,000,000.");
  if (min === max) throw new EmptyOptionsError("Min and max need to be different.");
  return { min: Math.min(min, max), max: Math.max(min, max) };
}

export function pickNumber(min: number, max: number): ToolOutcome {
  return { result: String(randomInt(min, max)), detail: `between ${min} and ${max}`, anim: "slot", reel: numberReel(min, max) };
}

export function higherOrLower(current: number, guess: "higher" | "lower"): ToolOutcome & { next: number; correct: boolean } {
  let next = randomInt(1, 100);
  while (next === current) next = randomInt(1, 100);
  const correct = guess === "higher" ? next > current : next < current;
  return {
    next,
    correct,
    result: String(next),
    detail: correct ? `${guess.toUpperCase()} it was! You called it.` : `Nope — it went ${guess === "higher" ? "LOWER" : "HIGHER"}.`,
    anim: "slot",
    reel: numberReel(1, 100),
  };
}
